import RightRail from '../../libs/gs-component-right-rail';
import { Img, Picture } from '@xo-union/tk-component-picture';
import { DataProxy } from 'apollo-cache';
import { Route } from 'react-router-dom';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
  PagesFragment,
  UpdateThemeMutation,
  usePagesQuery,
  useUpdateThemeMutation,
  useWeddingWebsiteQuery,
  WeddingWebsiteQuery,
} from '../../generated/graphql';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import { previewRoute, previewThemeRoute, previewThemeRouteMatch, manageRsvpSettingsRoute } from '../../helpers/routes';
import useQueryString from '../../helpers/useQueryString';
import { TrackingContext } from '../../TrackingContext';
import { BasicInfoRail } from '../BasicInfoRail';
import { weddingWebsiteQuery } from '../Dashboard';
import DoubleActionWithError from '../FormComponents/DoubleActionWithError';
import { ManageRsvpSettingsRail } from '../ManageRsvpSettingsRail';
import FullScreenModal from '../Modals/FullScreenModal';
import { RightRailContext } from '../PageItemsRail';
import PageItemRoutes from '../PageItemsRail/PageItemRoutes';
import { PageSettingsRail } from '../PageSettingsRail';
import { selectedThemeQuery } from '../ThemeSettings/graphql';
import ChangeTheme from './ChangeTheme';
import { lookupTheme, lookupThemeFamily } from './ChangeThemeRail';
import EditPageRail from './EditPageRail';
import styles from './liteSitePreview.scss';
import PreviewHeader from './PreviewHeader';
import { Theme } from './ThemeCard';
import ThemeInfo from './ThemeInfo';
import VariantContext from '../../VariantContext';
import useIsLiteSite from '../../helpers/useIsLiteSite';
import { useWorkflowUnstartedState } from '../RsvpPageCard/utils';
import AddPageRail from './AddPageRail';
import useBuildThemeAnalyticsBaseProps from '../../hooks/useBuildThemeAnalyticsBaseProps';

export type PreviewState = 'mobile' | 'desktop' | 'paper';

export const updateThemeSuccessCallback = (
  cache: DataProxy,
  data: UpdateThemeMutation | null | undefined,
  newThemeId: number
) => {
  const { weddingWebsite, ...rest } = cache.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;
  const updatedWeddingWebsite = { ...weddingWebsite, themeId: newThemeId };
  cache.writeQuery({
    query: weddingWebsiteQuery,
    data: { weddingWebsite: updatedWeddingWebsite, ...rest },
  });
  cache.writeQuery({
    query: selectedThemeQuery,
    data: { theme: data?.updateSelectedTheme },
  });
};

const RSVP_PAGE = 'rsvp';

const LiteSitePreview: React.FC = () => {
  // url hooks
  const history = useHistory();
  const match = useRouteMatch<{ themeId: string }>(previewThemeRouteMatch);
  const queryString = useQueryString();
  const urlTheme = match?.params.themeId ? parseInt(match.params.themeId) : null;
  const urlLocation = queryString.get('location');
  const railOpen = !!queryString.get('open');
  const mobile = !!queryString.get('mobile');

  // gql hooks
  const { data: wwsData, loading: wwsLoading } = useWeddingWebsiteQuery();
  const [updateTheme, { loading: updatingTheme }] = useUpdateThemeMutation();
  const { data } = usePagesQuery();
  const pages = data?.pages || [];

  // Theme Data
  const { themes } = useContext(VariantContext);

  // custom hooks
  const liteSite = useIsLiteSite();
  const { getThemeAnalyticsBaseProps } = useBuildThemeAnalyticsBaseProps();

  const { isUnstartedState } = useWorkflowUnstartedState();

  // state hooks
  const [selectedPreview, setSelectedPreview] = useState<PreviewState>(mobile ? 'mobile' : 'desktop');
  const [rightRailOpen, setRightRailOpen] = useState(!!urlTheme || railOpen);
  // default open rail on preview for: rsvp (page card experiment)
  const [editPageOpen, setEditPageOpen] = useState(urlLocation === RSVP_PAGE);
  const [previewLocation, setPreviewLocation] = useState(urlLocation || '');
  const [addPageOpen, setAddPageOpen] = useState<boolean>(false);
  const [addNewPage, setAddNewPage] = useState<PagesFragment>();
  const [showRsvpEmptyState, setShowRsvpEmptyState] = useState<boolean>(false);

  // set pseudo rsvp page when on rsvp
  const [currentPage, setCurrentPage] = useState<PagesFragment | string | undefined>(() => {
    // don't show the RSVP preview page if its unstarted
    if (previewLocation === RSVP_PAGE && isUnstartedState) {
      setPreviewLocation('');
    }
    return previewLocation === RSVP_PAGE ? RSVP_PAGE : pages.find(p => p.routeName === previewLocation);
  });
  const [previewThemeId, setPreviewThemeId] = useState(urlTheme);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Look up the paper suite image and designer URL.
  const { paperSuiteImageId, designerUrl } = useMemo(() => {
    const theme = lookupTheme(previewThemeId || wwsData?.weddingWebsite?.themeId || 0, themes);
    return {
      paperSuiteImageId: theme?.stationaryPreviewImage?.mediaApiId,
      designerUrl: theme?.designerUrl || '',
    };
  }, [wwsData, themes, previewThemeId]);

  useEffect(() => {
    // If we update the preview theme, and that does not have an associated paper suite image
    // Move away from the paper preview.
    if (!paperSuiteImageId && selectedPreview === 'paper') {
      setSelectedPreview('desktop');
    }
  }, [paperSuiteImageId, selectedPreview]);

  // Right Rail Context stuff
  const [overlayEnabled, setOverlayEnabled] = useState<boolean>(false);
  const [previewOnClose, setPreviewOnClose] = useState<boolean>(false);

  const disableOverlay = () => setOverlayEnabled(false);
  const enableOverlay = () => setOverlayEnabled(true);
  const previewAfterSave = () => setPreviewOnClose(true);
  const closeAfterSave = () => setPreviewOnClose(false);

  // analytics
  const { track, setPagesLoaded } = useContext(TrackingContext);
  useEffect(() => {
    setPagesLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // construct preview url
  const themeParam = previewThemeId ? `&themeId=${String(previewThemeId)}` : '';

  // Uncomment to test locally with wws-guest-view running
  // const previewUrl = `${'http://qa-dev.theknot.com:3001'}/us/${
  //   wwsData?.weddingWebsite?.slug
  // }/${previewLocation}?preview=true${themeParam}`;

  const previewUrl = `/us/${wwsData?.weddingWebsite?.slug}/${previewLocation}?preview=true${themeParam}`;

  // construct misc display strings
  const displayUrl = `${wwsData?.weddingWebsite?.vanityRoot}${wwsData?.weddingWebsite?.vanityUrl ||
    wwsData?.weddingWebsite?.slug}`;
  const goToPreviewPage = () => history.replace(previewRoute);

  const toggleRightRailHandler = () => {
    if (rightRailOpen) {
      setRightRailOpen(false);
    } else {
      if (selectedPreview === 'paper') {
        setSelectedPreview('desktop');
      }
      setRightRailOpen(true);
      track('WWS Preview Interaction', { selection: 'browse themes' });
    }
    setShowErrorToast(false);
    setIsAnimating(true);
  };

  const setRsvpEmptyStateHandler = (state: boolean) => {
    if (state) {
      setEditPageOpen(state);
    }
    setShowRsvpEmptyState(state);
  };

  const toggleEditPageHandler = () => {
    if (editPageOpen) {
      setEditPageOpen(false);
    } else {
      if (selectedPreview === 'paper') {
        setSelectedPreview('desktop');
      }
      setEditPageOpen(true);
      track('WWS Preview Interaction', { selection: 'edit page' });
    }
    setShowErrorToast(false);
    setIsAnimating(true);
  };

  const toggleAddPageHandler = (page?: PagesFragment) => {
    setAddPageOpen(!addPageOpen);
    if (page) {
      setAddNewPage(page);
    }
  };

  const toggleMobileDesktopCallback = (selectId: string) => {
    switch (selectId) {
      case 'category-inv':
        setSelectedPreview('paper');
        setEditPageOpen(false);
        setRightRailOpen(false);
        break;
      case 'desktop':
        setSelectedPreview('desktop');
        break;
      case 'mobile':
        setSelectedPreview('mobile');
        break;
    }
    track('WWS Preview Interaction', { selection: selectId });
  };

  const previewThemeCallback = (themeId: number) => {
    setPreviewThemeId(themeId);
    setPreviewLocation('');
    setCurrentPage(pages.find(p => p.routeName === ''));
    history.replace(previewThemeRoute(themeId));
  };

  const resetThemeToCurrent = (closeRail: boolean = true) => {
    setPreviewThemeId(null);
    setPreviewLocation('');
    setCurrentPage(pages.find(p => p.routeName === ''));
    goToPreviewPage();
    if (rightRailOpen && closeRail) {
      toggleRightRailHandler();
    }
  };

  const trackDesignSelection = (previousTheme: Theme | null | undefined, newThemeId: number) => {
    const newTheme = lookupTheme(newThemeId, themes || []);
    track('Design Selection', {
      ...getThemeAnalyticsBaseProps(newTheme),
      previousTheme: previousTheme?.name,
      colorwayUpdate: !!previousTheme?.family && previousTheme.family === newTheme?.family,
    });
  };

  const trackPaperClick = () => {
    const theme = lookupTheme(previewThemeId || wwsData?.weddingWebsite.themeId || 0, themes);
    track('Matching Invitation links', {
      source: 'preview',
      themeName: theme?.name,
      page: 'preview',
      partnerName: theme?.themeDesigner?.companyName,
    });
  };

  const setThemeToCurrentPreview = async () => {
    if (previewThemeId && !updatingTheme) {
      const previousTheme =
        wwsData?.weddingWebsite && themes ? lookupTheme(wwsData?.weddingWebsite.themeId, themes) : null;
      setShowErrorToast(false);
      await updateTheme({
        variables: {
          id: previewThemeId,
        },
        update(cache, { data }) {
          updateThemeSuccessCallback(cache, data, previewThemeId);
        },
      })
        .then(() => {
          trackDesignSelection(previousTheme, previewThemeId);
          resetThemeToCurrent();
        })
        .catch(() => {
          setShowErrorToast(true);
        });
    }
  };

  // Automatically open edit page
  useEffect(() => {
    if (currentPage === RSVP_PAGE) {
      setEditPageOpen(true);

      if (isUnstartedState) {
        setShowRsvpEmptyState(true);
      }
    }
  }, [currentPage, isUnstartedState]);

  /*
   * Only animate the iframe when opening or closing the right rail (not when switching between mobile and desktop)
   */
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isAnimating) {
      timeout = setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isAnimating]);

  /*
   * Don't push theme preview updates of the iframe src to the browser back button
   */
  useEffect(() => {
    iframeRef?.current?.contentWindow?.location.replace(previewUrl);
  }, [previewUrl, iframeRef]);

  const onMessage = useCallback(
    ({ data }: MessageEvent) => {
      if (data) {
        try {
          const { data: page } = JSON.parse(data);
          if (page === RSVP_PAGE) {
            setCurrentPage(RSVP_PAGE);
          } else if (page?.__typename && page.__typename === 'WWS_Page') {
            // Reload page if coming from RSVP
            if (currentPage === RSVP_PAGE) {
              iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({
                  type: 'reload',
                }),
                getTargetOrigin()
              );
            }
            setCurrentPage(page);
          }
        } catch (error) {
          // Ignore the error?
        }
      }
    },
    [currentPage]
  );

  /*
   * Updates local state when the page in the iframe changes.
   */
  useEffect(() => {
    if (history.location.pathname !== previewRoute) {
      history.push(previewRoute);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [history, onMessage]);

  const iframeContainerClasses = () => {
    return `${styles.iframeContainer} ${styles[selectedPreview]} ${
      editPageOpen || rightRailOpen ? styles.rightRailOpen : ''
    } ${isAnimating ? styles.animating : ''} ${!!previewThemeId ? styles.iframePreviewActive : ''}`;
  };

  return (
    <FullScreenModal className={selectedPreview === 'paper' ? styles.paperSuiteModal : ''}>
      <div data-testid="preview-page" className={styles.previewPage}>
        <PreviewHeader
          iframeRef={iframeRef}
          pages={pages}
          url={wwsLoading ? '' : displayUrl}
          currentPage={currentPage as PagesFragment}
          setCurrentPage={setCurrentPage}
          allowPaperPreview={paperSuiteImageId ? true : false}
          selectedPreview={selectedPreview}
          changeSelectedPreview={toggleMobileDesktopCallback}
          rightRailToggle={toggleRightRailHandler}
          editPageToggle={toggleEditPageHandler}
          addPageToggle={toggleAddPageHandler}
          showRsvpEmptyState={showRsvpEmptyState ? true : false}
          setShowRsvpEmptyState={setRsvpEmptyStateHandler}
          className={`${styles.previewHeader} ${editPageOpen || rightRailOpen ? styles.rightRailOpen : ''}`}
        />
        {selectedPreview === 'paper' ? (
          <div className={styles.paperSuiteContainer}>
            <div className={styles.paperSuiteContent}>
              <div className={styles.paperSuitePhotoContainer}>
                <div className={styles.paperSuiteImg}>
                  <Picture id={paperSuiteImageId}>
                    <Img />
                  </Picture>
                </div>
              </div>
              <div className={styles.paperSuiteTextContainer}>
                <div className={styles.paperSuiteText}>
                  <span>Get three free stationery samples that match your Wedding Website with promo code 3FREE.</span>
                  <br />
                  <br />
                  <a target="_blank" onClick={trackPaperClick} rel="noopener noreferrer" href={designerUrl || ''}>
                    View Matching Designs on The Knot
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={iframeContainerClasses()}>
            {wwsLoading ? (
              <div />
            ) : (
              <div className={styles.iframeSizer}>
                <div className={styles.iframeWrap}>
                  <iframe
                    id="preview-iframe"
                    title="preview-iframe"
                    src={previewUrl}
                    className={(editPageOpen || rightRailOpen) && selectedPreview === 'desktop' ? styles.scaled : ''}
                    ref={iframeRef}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <ChangeTheme
          previewThemeId={previewThemeId}
          resetThemeToCurrent={resetThemeToCurrent}
          rightRailOpen={rightRailOpen}
          previewThemeCallback={previewThemeCallback}
          updatingTheme={updatingTheme}
          showErrorToast={showErrorToast}
          setThemeToCurrentPreview={setThemeToCurrentPreview}
          wwsData={wwsData}
          onEscapeKey={() => setRightRailOpen(false)}
        />

        <RightRail open={editPageOpen || showRsvpEmptyState} overlay={overlayEnabled} className={styles.rightRail}>
          <RightRailContext.Provider
            value={{ disableOverlay, enableOverlay, previewOnClose, previewAfterSave, closeAfterSave, iframeRef }}
          >
            {currentPage === RSVP_PAGE || showRsvpEmptyState ? (
              <ManageRsvpSettingsRail
                preview
                onClose={() => {
                  setEditPageOpen(false);
                  setShowRsvpEmptyState(false);
                }}
                onSaveSuccess={() =>
                  iframeRef.current?.contentWindow?.postMessage(
                    JSON.stringify({
                      type: 'reload',
                    }),
                    getTargetOrigin()
                  )
                }
              />
            ) : (
              <>
                <PageItemRoutes preview>
                  <Route path={manageRsvpSettingsRoute(true)}>
                    <ManageRsvpSettingsRail preview onClose={goToPreviewPage} onSaveSuccess={goToPreviewPage} />
                  </Route>
                  {typeof currentPage !== 'string' && (
                    <EditPageRail pageId={currentPage?.id} setEditPageOpen={setEditPageOpen} iframeRef={iframeRef} />
                  )}
                </PageItemRoutes>
              </>
            )}
          </RightRailContext.Provider>
        </RightRail>
        <PageSettingsRail preview className={styles.rightRail} />
        <BasicInfoRail className={styles.rightRail} iframeRef={iframeRef} liteSite={liteSite} />
        {!!previewThemeId && themes ? (
          <div className={styles.mobileWebPreviewFooter}>
            <ThemeInfo
              themeFamily={lookupThemeFamily(previewThemeId, themes)}
              thumbnailTheme={lookupTheme(previewThemeId, themes)}
              onClick={previewThemeCallback}
              selected={previewThemeId}
            />
            <DoubleActionWithError
              primaryOnClick={setThemeToCurrentPreview}
              secondaryOnClick={() => {
                resetThemeToCurrent(false);
              }}
              errorText="Something went wrong! Your theme has not been updated."
              showSpinner={updatingTheme}
              showError={showErrorToast}
              secondaryText="Cancel"
            />
          </div>
        ) : null}
      </div>
      <>
        <RightRail open={addPageOpen} overlay>
          <RightRailContext.Provider
            value={{
              disableOverlay,
              enableOverlay,
              previewOnClose,
              previewAfterSave,
              closeAfterSave,
              iframeRef,
            }}
          >
            <AddPageRail
              setCurrentPage={setCurrentPage}
              iframeRef={iframeRef}
              toggleAddPageHandler={toggleAddPageHandler}
              addPage={addNewPage}
            />
          </RightRailContext.Provider>
        </RightRail>
      </>
    </FullScreenModal>
  );
};

export default LiteSitePreview;
